<script setup lang="ts">
import type { Translate } from '@/plugins/i18n/types';
import { inject, reactive, ref, computed } from 'vue';
import Bugsnag from '@bugsnag/js';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toastification';

import Tip from '@/components/tip/Tip.vue';

import { ALAvatar } from '@af-lib/vue-ui';
import { useALAvatar } from '@/features/avatar/useALAvatar';

import AvatarCropper from '@/features/avatar/AvatarCropper.vue';
import { useAvatarStore } from '@/features/avatar/avatarStore';
import { useMediaStore } from '@/features/overlays/modals/media/mediaStore';

import { useAvatarMutation } from '@/api/mutations/user/useAvatarMutation';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { useUserQuery } from '@/api/queries/user/useUserQuery';

import { imageSize } from '@/helpers/image';
import { timeout } from '@/helpers/timeout';
import { MixpanelInstance } from '@/plugins/mixpanel/mixpanel';
import router from '@/router';

import { useGlobalStore } from '@/stores/global';

const t = inject('t') as Translate;
const toast = useToast();
const globalStore = useGlobalStore();

const { auth, refetch: refetchAuth } = useMeQuery();
const { refetch: refetchUser } = useUserQuery();

const { avatarUrl, firstName, lastName } = useALAvatar(auth, { imgSize: 'large' });

const avatarToUpload = ref<Blob>();
const { updateAvatar } = useAvatarMutation(avatarToUpload);

const { isAvatarLoading, isDefaultAvatar } = storeToRefs(useAvatarStore());
const { showOnlyAvatarModal } = storeToRefs(useMediaStore());

const editMode = ref(false);
const loader = ref(0);
const image = reactive({
  src: '',
  type: 'image/jpeg',
});
const fileInput = ref<HTMLInputElement | null>(null);

const isNewAvatarLoaded = computed(
  () => !isDefaultAvatar.value && !!showOnlyAvatarModal.value && !isAvatarLoading.value,
);

const triggerFileUpload = () => {
  if (!fileInput.value) return;
  fileInput.value.click();
};

const handleFileUpload = async (e: Event) => {
  if (isAvatarLoading.value) return;

  const input = e.target as HTMLInputElement;
  const files = input.files;

  if (files && files[0]) {
    if (!files[0].type.includes('image')) {
      return toast.error(t('candidate_profile.picture.error.wrong_file_format'));
    }

    if (files[0].size > 5242880) {
      return toast.error(t('candidate_profile.picture.error.wrong_size'));
    }

    const imgUrl = URL.createObjectURL(files[0]);
    const { width, height } = await imageSize(imgUrl);
    if (width < 220 || height < 220) {
      return toast.error(t('candidate_profile.picture.error.dimension'));
    }

    image.src = imgUrl;
    image.type = files[0].type;
    editMode.value = true;
  }
};

async function updatePicture(blob: Blob | undefined, closeFn?: () => void) {
  avatarToUpload.value = blob;

  loader.value = 0;
  startLoader();
  editMode.value = false;
  isAvatarLoading.value = true;

  try {
    await updateAvatar();
    // timeout is used to give enough time for AWS S3 to process the uploading.
    if (blob) await timeout(8000);
    await Promise.all([refetchAuth(), refetchUser()]);
  } catch (e) {
    Bugsnag.notify(e as any);
    toast.error(t('general.error.general'));
  } finally {
    /** REPORT **/
    if (blob) {
      MixpanelInstance.track('profile_photo_update', {
        origin: router.currentRoute.value.matched[0].name,
      });
    } else {
      MixpanelInstance.track('profile_photo_delete', {
        origin: router.currentRoute.value.matched[0].name,
      });
    }

    isAvatarLoading.value = false;
    if (closeFn) closeFn();
  }
}

function startLoader() {
  const interval = setInterval(() => {
    loader.value += 1;
    if (loader.value >= 100) clearInterval(interval);
  }, 80);
}
</script>

<template>
  <!-- DISPLAY MODE -->
  <div v-if="!editMode" class="flex flex-col items-center p-6">
    <h2 class="text-h2 my-4">{{ $t('profile.photo.profile.title') }}</h2>
    <p class="text-body-2 text-center">
      {{ $t('profile.photo.profile.body') }}
    </p>

    <!-- AVATAR UPLOAD -->
    <label
      for="avatar-file"
      class="relative my-10 cursor-pointer"
      :aria-label="$t('general.upload')"
      :title="$t('general.upload')"
    >
      <ALAvatar
        :avatar-url="isDefaultAvatar ? '' : avatarUrl"
        :firstName="firstName"
        :lastName="lastName"
        :gradients="undefined"
        size="xxxl"
      />

      <!-- A11Y UPLOAD -->
      <button
        type="button"
        class="inline-flex items-center justify-center gap-2 text-sm font-medium outline-hidden duration-300 focus-visible:ring-2 focus-visible:ring-afblue-600 focus-visible:ring-offset-2 border border-afgrey-500 text-afgrey-700 hover:bg-afgrey-300 hover:border-afgrey-300 disabled:bg-white disabled:border-afgrey-500 disabled:text-afgrey-500 active:bg-afgrey-300 rounded-full p-0 min-w-0 absolute bottom-0 end-0 translate-x-1/4 translate-y-1/4 rtl:-translate-x-1/4 z-10 h-14 w-14 bg-white cursor-pointer focus-within:ring-2 focus-within:ring-afblue-600"
        :disabled="isAvatarLoading"
        @click.prevent="triggerFileUpload"
      >
        <v-icon
          v-if="isAvatarLoading"
          icon="fas fa-spinner-third"
          class="animate-spin"
          :size="19"
        />
        <v-icon v-else icon="fas fa-camera" color="iconSolid" :size="19" />
      </button>
    </label>

    <!-- HIDDEN INPUT -->
    <input
      ref="fileInput"
      type="file"
      @change="handleFileUpload"
      name="file"
      id="avatar-file"
      accept=".jpg, .jpeg, .png"
      class="hidden"
    />

    <p v-if="isNewAvatarLoaded" class="text-body-3 font-medium text-teal-darken-1 mb-5">
      {{ $t('profile.photo.profile.success') }}
    </p>

    <Tip
      :imgUrl="`${globalStore.assets_cdn_url}/share/avatar-tip.png`"
      :text="$t('profile.photo.profile.tip')"
    />
  </div>

  <!-- EDIT MODE -->
  <div v-else class="flex flex-col items-center py-6 px-6">
    <AvatarCropper
      :file="image.src"
      :type="image.type"
      @save="updatePicture"
      @cancel="editMode = false"
    />
  </div>
</template>
